"use client";

import { useEffect, useState } from "react";
import { usePathname } from "next/navigation";
import dynamic from "next/dynamic";
import Deferred from "./DeferredComponents";

const LiveTicker = dynamic(() => import("./LiveTicker"), { ssr: false });
const PinnedBoxes = dynamic(() => import("./PinnedBoxes"), { ssr: false });
const ClientOnlyModal = dynamic(() => import("@/app/components/ClientOnlyModal"), { ssr: false });
const CookieBanner = dynamic(() => import("@/app/components/CookieBanner"), { ssr: false });
const NotificationsWrapper = dynamic(() => import("./NotificationsWrapper"), { ssr: false });
const ServiceWorkerRegistration = dynamic(() => import("./ServiceWorkerRegistration"), { ssr: false });
const PushNotificationPrompt = dynamic(() => import("./PushNotificationPrompt"), { ssr: false });
const AutoOfflineBanner = dynamic(() => import("@/components/OfflineBanner").then(m => ({ default: m.AutoOfflineBanner })), { ssr: false });
// Replaced by OnboardingProvider (premium first-run modal + sutil floating badge)
// const TradingDisabledBanner = dynamic(() => import("./TradingDisabledBanner"), { ssr: false });
// OnboardingProvider is NOT inside <Deferred>: it has to race against
// Privy link-account modals and lost when waiting for
// requestIdleCallback. Mounted via AppLayoutWithSidebar instead.
//
// 2026-06-03: FrenPickerGate removed from the signup critical path. Picking
// a Fren is no longer a mandatory first-run step — it's optional from the
// profile ("Change avatar" → FrenAvatarPicker). New users without a Fren
// fall back to GradientAvatar (a unique per-wallet gradient), so nobody
// looks generic. This also kills the fragile two-modal sync that gated the
// OnboardingModal on `fren_pfp`.

/**
 * Client component that wraps all non-critical deferred components.
 * Moved here because Next.js 16 disallows `ssr: false` in Server Components.
 *
 * Skipped entirely inside /embed/* or any foreign iframe — third-party
 * hosts (Discord Activity, dextools) expect bare content, not our
 * LiveTicker / PinnedBoxes / CookieBanner etc.
 */
export default function DeferredLayout() {
  const pathname = usePathname();
  const [isInIframe, setIsInIframe] = useState(false);
  useEffect(() => {
    try {
      setIsInIframe(typeof window !== "undefined" && window.self !== window.top);
    } catch {
      setIsInIframe(true);
    }
  }, []);
  if (pathname?.startsWith("/embed") || isInIframe) return null;

  return (
    <>
      {/* LiveTicker is part of the visible chrome (footer pill, alongside
          sidebar) so we render it outside the idle-callback gate — the
          ~1-3s delay was noticeable. Still dynamic-imported with no SSR,
          so it doesn't block initial paint, just lands a frame or two
          after first commit. */}
      <LiveTicker />
      <Deferred>
        <AutoOfflineBanner />
        <ServiceWorkerRegistration />
        <PushNotificationPrompt />
        <PinnedBoxes />
        <ClientOnlyModal />
        <CookieBanner />
        <NotificationsWrapper />
      </Deferred>
    </>
  );
}
